<template>
  <v-dialog :value="value" max-width="560" scrollable @input="$emit('input', $event)">
    <v-card>
      <v-card-title class="font-md">
        Теги клиентов
        <v-spacer />
        <v-btn icon small @click="$emit('input', false)"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>
      <v-card-subtitle class="pb-2">
        Теги видны только администраторам. Один тег можно назначать нескольким клиентам.
      </v-card-subtitle>

      <v-card-text style="max-height: 60vh">
        <v-form class="d-flex align-start mb-4" style="gap: 8px" @submit.prevent="create">
          <v-text-field
            v-model="newName" label="Новый тег" outlined dense counter="255" :error-messages="newError"
            :disabled="busy" @input="newError = []"
          />
          <v-btn color="primary" type="submit" :loading="creating" :disabled="busy">Добавить</v-btn>
        </v-form>

        <StateBlock v-if="loading && !tags.length" state="loading" compact />
        <StateBlock v-else-if="error" state="error" compact text="Не удалось загрузить теги." @retry="reload" />
        <StateBlock v-else-if="!tags.length" state="empty" compact icon="mdi-tag-outline" title="Тегов пока нет" />

        <v-list v-else dense class="pa-0">
          <v-list-item v-for="tag in tags" :key="tag.id" class="px-0">
            <template v-if="editing === tag.id">
              <v-text-field
                v-model="editName" dense outlined hide-details="auto" counter="255" autofocus
                :error-messages="editError" :disabled="busy"
                @input="editError = []" @keyup.enter="rename(tag)" @keyup.esc="cancelEdit"
              />
              <v-btn icon small color="primary" :loading="saving" :disabled="busy" title="Сохранить" @click="rename(tag)">
                <v-icon small>mdi-check</v-icon>
              </v-btn>
              <v-btn icon small :disabled="busy" title="Отмена" @click="cancelEdit"><v-icon small>mdi-close</v-icon></v-btn>
            </template>
            <template v-else>
              <v-list-item-content>
                <v-list-item-title>{{ tag.name }}</v-list-item-title>
              </v-list-item-content>
              <v-btn icon small :disabled="busy" title="Переименовать" @click="startEdit(tag)"><v-icon small>mdi-pencil</v-icon></v-btn>
              <v-btn icon small color="error" :disabled="busy" title="Удалить" @click="askRemove(tag)"><v-icon small>mdi-delete-outline</v-icon></v-btn>
            </template>
          </v-list-item>
        </v-list>
      </v-card-text>
    </v-card>

    <v-dialog v-model="confirm.show" max-width="420" :persistent="removing">
      <v-card>
        <v-card-title class="font-md">Удалить тег «{{ confirm.tag && confirm.tag.name }}»?</v-card-title>
        <v-card-text>
          Тег исчезнет у всех клиентов, которым он назначен. Это действие нельзя отменить.
          <v-alert v-if="confirm.error" type="error" dense outlined class="mt-3 mb-0">{{ confirm.error }}</v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="removing" @click="confirm.show = false">Отмена</v-btn>
          <v-btn color="error" :loading="removing" @click="remove">Удалить</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import StateBlock from '@/components/ui/StateBlock.vue';

// Текст ошибки поля name из ответа сервера (400): уникальность без учёта регистра, пустое, длина
const nameErrors = (err) => {
  const messages = err && err.data && err.data.name;
  if (Array.isArray(messages)) return messages;
  if (typeof messages === 'string') return [messages];
  return ['Не удалось сохранить тег. Попробуйте ещё раз.'];
};

// Управление справочником тегов корпов: добавить, переименовать, удалить.
// Окно открывается на странице списка, поэтому после изменений достаточно обновить справочник и список клиентов.
export default {
  name: 'CorpTagsManager',
  components: { StateBlock },
  props: {
    value: { type: Boolean, default: false },
  },
  data: () => ({
    newName: '',
    newError: [],
    creating: false,
    editing: null,
    editName: '',
    editError: [],
    saving: false,
    removing: false,
    confirm: { show: false, tag: null, error: '' },
  }),
  computed: {
    ...mapGetters({
      tags: 'users/corpClientTags/tags',
      loading: 'users/corpClientTags/loading',
      error: 'users/corpClientTags/error',
      filter: 'users/clients/filter',
    }),
    busy() {
      return this.creating || this.saving || this.removing;
    },
  },
  watch: {
    value(open) {
      if (open) {
        this.newName = '';
        this.newError = [];
        this.cancelEdit();
      }
    },
  },
  methods: {
    ...mapActions({
      reload: 'users/corpClientTags/load',
      createTag: 'users/corpClientTags/create',
      renameTag: 'users/corpClientTags/rename',
      removeTag: 'users/corpClientTags/remove',
      loadClientsFilter: 'users/clients/loadClientsFilter',
      setFilterValue: 'users/clients/setFilterValue',
    }),
    // Названия тегов лежат в строках списка: после правок справочника перечитываем список
    async refreshClients() {
      try {
        await this.loadClientsFilter();
      } catch (e) {
        // ошибку списка показывает сам список
      }
    },
    startEdit(tag) {
      this.editing = tag.id;
      this.editName = tag.name;
      this.editError = [];
    },
    cancelEdit() {
      this.editing = null;
      this.editName = '';
      this.editError = [];
    },
    async create() {
      const name = this.newName.trim();
      if (!name) {
        this.newError = ['Введите название тега'];
        return;
      }
      if (name.length > 255) {
        this.newError = ['Не больше 255 символов'];
        return;
      }
      this.creating = true;
      try {
        await this.createTag(name);
        this.newName = '';
        this.$toast.success('Тег добавлен', '', { position: 'topRight' });
      } catch (err) {
        this.newError = nameErrors(err);
      } finally {
        this.creating = false;
      }
    },
    async rename(tag) {
      const name = this.editName.trim();
      if (!name) {
        this.editError = ['Введите название тега'];
        return;
      }
      if (name.length > 255) {
        this.editError = ['Не больше 255 символов'];
        return;
      }
      if (name === tag.name) {
        this.cancelEdit();
        return;
      }
      this.saving = true;
      try {
        await this.renameTag({ id: tag.id, name });
        this.cancelEdit();
        await this.refreshClients();
        this.$toast.success('Тег переименован', '', { position: 'topRight' });
      } catch (err) {
        if (err && err.status === 404) {
          // Тег удалили параллельно: обновляем справочник
          this.cancelEdit();
          await this.reload();
          this.$toast.warning('Этот тег уже удалён, список обновлён', '', { position: 'topRight' });
        } else {
          this.editError = nameErrors(err);
        }
      } finally {
        this.saving = false;
      }
    },
    askRemove(tag) {
      this.confirm = { show: true, tag, error: '' };
    },
    async remove() {
      const { tag } = this.confirm;
      this.removing = true;
      this.confirm.error = '';
      try {
        await this.removeTag(tag.id);
        this.confirm.show = false;
        // Удалённый тег не должен остаться в фильтре списка
        if (Array.isArray(this.filter.tags) && this.filter.tags.includes(tag.id)) {
          await this.setFilterValue({ key: 'tags', value: this.filter.tags.filter((id) => id !== tag.id) });
        } else {
          await this.refreshClients();
        }
        this.$toast.success('Тег удалён', '', { position: 'topRight' });
      } catch (err) {
        if (err && err.status === 404) {
          this.confirm.show = false;
          await this.reload();
          await this.refreshClients();
        } else {
          this.confirm.error = 'Не удалось удалить тег. Попробуйте ещё раз.';
        }
      } finally {
        this.removing = false;
      }
    },
  },
};
</script>
